import { useQuery } from "@tanstack/react-query";
import { contentStudioClient } from "~/clients/ContentStudio";
import {
  ALL_RESOURCE_VISIBILITY,
  ResourceVisibility,
} from "~/core/ResourceVisibility";
import { localizedFieldsProjection } from "~/core/api/Sanity/localize";
import { buildGroqQuery } from "~/core/api/Sanity/query";
import { AVAILABLE_LOCALES } from "~/core/locale";
import { LogePage } from "../Domain/LogePage";
import { mapToLogePageDomain } from "./logePage.mapper";
import { LogePagesPayloadSchema } from "./logePagePayloadSchema";

const getLogePagesOfDestinationQuery = (locale: AVAILABLE_LOCALES) =>
  buildGroqQuery(`
    *[
      _type == 'logePage'
      && loge->destination._ref == $destinationId
      && visibility.value in $visibilities
      && !(_id in path('drafts.**'))
    ]{
      seoSlug{ current },
      gogaillePlacesAddressDetails{ ${localizedFieldsProjection(locale)} },
      gogaillePlacesDistancesDetails{ ${localizedFieldsProjection(locale)} },
      gogaillePlacesParkingsDetails{ ${localizedFieldsProjection(locale)} },
      accessibility {
        reducedMobilityRooms,
        elevator,
        stairliftPlatform
      },
      visibility,
      loge->{
        _id,
        pmsId{ current },
        name,
        building->{
          address{
            line1,
            line2,
            postalCode,
            city,
            coordinate{ lat, lng }
          }
        }
      }
    }
  `);

type FetchLogePagesOfDestinationQuery = {
  destinationId: string;
  visibilities?: Array<ResourceVisibility>;
  locale: AVAILABLE_LOCALES;
};

export const fetchLogePagesOfDestination = async ({
  destinationId,
  visibilities = ALL_RESOURCE_VISIBILITY,
  locale,
}: FetchLogePagesOfDestinationQuery): Promise<Array<LogePage>> => {
  const response = await contentStudioClient.fetch(
    getLogePagesOfDestinationQuery(locale),
    { destinationId, visibilities },
  );

  const logePagesPayload = LogePagesPayloadSchema.parse(response);

  return logePagesPayload.map((item) => mapToLogePageDomain(item, locale));
};

export function useLogePagesOfDestination(
  query: FetchLogePagesOfDestinationQuery,
  initialData?: Array<LogePage>,
): { logePages: Array<LogePage> | null } {
  const { data: logePages = null } = useQuery(
    ["offer-search-loge-pages-of-destination", query.destinationId],
    () => fetchLogePagesOfDestination(query),
    {
      initialData,
    },
  );

  return { logePages };
}
